/** Temas de fondo decorativo del shell autenticado (solo visual). */

export const APP_BACKGROUND_THEMES = {
  none: {
    id: "none",
    label: "Sin fondo (actual)",
    description: "Gris claro sobrio #F4F6F8, sin capa decorativa.",
  },
  contable_azul: {
    id: "contable_azul",
    label: "Contable azul",
    description: "Lavado azul-gris muy suave; tono profesional de despacho.",
  },
  contable_menta: {
    id: "contable_menta",
    label: "Contable menta",
    description: "Toque verde BNT casi imperceptible en esquinas.",
  },
  acceso_suave: {
    id: "acceso_suave",
    label: "Estilo acceso (suave)",
    description: "Geometría del login con opacidad muy baja.",
  },
  custom: {
    id: "custom",
    label: "Diseño propio",
    description: "Paleta y textura elegidas en la pestaña Personalizado.",
  },
};

export const APP_BACKGROUND_THEME_IDS = Object.keys(APP_BACKGROUND_THEMES);

export const DEFAULT_APP_BACKGROUND_THEME = "none";

/** Paletas para el tema «custom». */
export const APP_BACKGROUND_PALETTES = {
  azul_despacho: {
    id: "azul_despacho",
    label: "Azul despacho",
    description: "Azul corporativo BNT sobre gris claro.",
    base: "#F4F6F8",
    primary: "#0F2D5C",
    secondary: "#3B6BA5",
    wash: "#E8EEF5",
  },
  menta_bnt: {
    id: "menta_bnt",
    label: "Menta BNT",
    description: "Verde institucional casi imperceptible.",
    base: "#F4F6F8",
    primary: "#3DA873",
    secondary: "#0F2D5C",
    wash: "#EEF5F1",
  },
  pizarra: {
    id: "pizarra",
    label: "Pizarra",
    description: "Grises fríos de oficina.",
    base: "#F4F6F8",
    primary: "#475569",
    secondary: "#64748B",
    wash: "#EEF1F4",
  },
  arena_clara: {
    id: "arena_clara",
    label: "Arena clara",
    description: "Toque cálido muy suave (no satura tablas).",
    base: "#F5F4F2",
    primary: "#8B7355",
    secondary: "#A89070",
    wash: "#F0EDE8",
  },
  oceano: {
    id: "oceano",
    label: "Océano",
    description: "Azul-verde sereno de despacho costero.",
    base: "#F3F6F7",
    primary: "#0E4D5C",
    secondary: "#2A7A8A",
    wash: "#E6F0F2",
  },
};

export const APP_BACKGROUND_PALETTE_IDS = Object.keys(APP_BACKGROUND_PALETTES);

/** Texturas / patrones para el tema «custom». */
export const APP_BACKGROUND_TEXTURES = {
  lavado: {
    id: "lavado",
    label: "Lavado suave",
    description: "Gradiente + nubes difusas sin patrón.",
  },
  grano: {
    id: "grano",
    label: "Grano fino",
    description: "Textura de papel con ruido muy ligero.",
  },
  lino: {
    id: "lino",
    label: "Lino",
    description: "Líneas diagonales sutiles tipo tela.",
  },
  papel: {
    id: "papel",
    label: "Papel punteado",
    description: "Puntos regulares casi invisibles.",
  },
  geometria: {
    id: "geometria",
    label: "Geometría",
    description: "Bloques inclinados al estilo del acceso.",
  },
};

export const APP_BACKGROUND_TEXTURE_IDS = Object.keys(APP_BACKGROUND_TEXTURES);

export const APP_BACKGROUND_INTENSITIES = {
  suave: { id: "suave", label: "Suave", factor: 0.045 },
  media: { id: "media", label: "Media", factor: 0.075 },
  marcada: { id: "marcada", label: "Marcada", factor: 0.11 },
};

export const APP_BACKGROUND_INTENSITY_IDS = Object.keys(APP_BACKGROUND_INTENSITIES);

export const DEFAULT_APP_BACKGROUND_CUSTOM = {
  palette: "azul_despacho",
  texture: "lavado",
  intensity: "suave",
};

export function normalizeAppBackgroundTheme(value) {
  const id = String(value || "").trim();
  return APP_BACKGROUND_THEME_IDS.includes(id) ? id : DEFAULT_APP_BACKGROUND_THEME;
}

export function normalizeAppBackgroundCustom(value) {
  const src = value && typeof value === "object" ? value : {};
  const palette = String(src.palette || "").trim();
  const texture = String(src.texture || "").trim();
  const intensity = String(src.intensity || "").trim();
  return {
    palette: APP_BACKGROUND_PALETTE_IDS.includes(palette)
      ? palette
      : DEFAULT_APP_BACKGROUND_CUSTOM.palette,
    texture: APP_BACKGROUND_TEXTURE_IDS.includes(texture)
      ? texture
      : DEFAULT_APP_BACKGROUND_CUSTOM.texture,
    intensity: APP_BACKGROUND_INTENSITY_IDS.includes(intensity)
      ? intensity
      : DEFAULT_APP_BACKGROUND_CUSTOM.intensity,
  };
}

function hexToRgba(hex, alpha) {
  const raw = String(hex || "").replace("#", "").trim();
  if (raw.length !== 6) return `rgba(15, 45, 92, ${alpha})`;
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Estilos/capas para renderizar un fondo personalizado.
 * @returns {{ baseStyle: object, blobs: Array<{style: object}>, overlayStyle: object|null, geometry: Array<{style: object}> }}
 */
export function buildCustomBackgroundLayers(customInput) {
  const custom = normalizeAppBackgroundCustom(customInput);
  const palette = APP_BACKGROUND_PALETTES[custom.palette];
  const factor = APP_BACKGROUND_INTENSITIES[custom.intensity].factor;
  const a1 = factor;
  const a2 = factor * 0.85;
  const a3 = Math.min(factor * 1.2, 0.14);

  const baseStyle = {
    background: `linear-gradient(135deg, ${palette.wash} 0%, ${palette.base} 48%, ${palette.wash} 100%)`,
  };

  const blobs = [
    {
      style: {
        position: "absolute",
        top: "-6rem",
        right: "-5rem",
        width: "28rem",
        height: "28rem",
        borderRadius: "9999px",
        background: hexToRgba(palette.primary, a1),
        filter: "blur(64px)",
      },
    },
    {
      style: {
        position: "absolute",
        bottom: "-5rem",
        left: "-2.5rem",
        width: "22rem",
        height: "22rem",
        borderRadius: "9999px",
        background: hexToRgba(palette.secondary, a2),
        filter: "blur(56px)",
      },
    },
  ];

  let overlayStyle = null;
  const geometry = [];

  if (custom.texture === "grano") {
    overlayStyle = {
      position: "absolute",
      inset: 0,
      opacity: Math.min(0.35 + factor * 2, 0.55),
      backgroundImage:
        "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.45'/%3E%3C/svg%3E\")",
      backgroundRepeat: "repeat",
      backgroundSize: "180px 180px",
      mixBlendMode: "multiply",
    };
  } else if (custom.texture === "lino") {
    overlayStyle = {
      position: "absolute",
      inset: 0,
      opacity: Math.min(0.2 + factor * 1.5, 0.4),
      backgroundImage: `repeating-linear-gradient(
        45deg,
        ${hexToRgba(palette.primary, 0.04)} 0px,
        ${hexToRgba(palette.primary, 0.04)} 1px,
        transparent 1px,
        transparent 10px
      )`,
    };
  } else if (custom.texture === "papel") {
    overlayStyle = {
      position: "absolute",
      inset: 0,
      opacity: Math.min(0.25 + factor * 1.8, 0.45),
      backgroundImage: `radial-gradient(${hexToRgba(palette.primary, 0.18)} 0.6px, transparent 0.7px)`,
      backgroundSize: "14px 14px",
    };
  } else if (custom.texture === "geometria") {
    geometry.push(
      {
        style: {
          position: "absolute",
          top: "-5rem",
          right: "-4rem",
          width: "24rem",
          height: "24rem",
          background: hexToRgba(palette.primary, a3),
          transform: "rotate(12deg)",
          borderRadius: "0.375rem",
        },
      },
      {
        style: {
          position: "absolute",
          top: "10rem",
          right: "-2rem",
          width: "16rem",
          height: "11rem",
          background: hexToRgba(palette.secondary, a1),
          transform: "rotate(-6deg)",
          borderRadius: "0.375rem",
        },
      },
      {
        style: {
          position: "absolute",
          bottom: "-6rem",
          left: "-3rem",
          width: "18rem",
          height: "11rem",
          background: hexToRgba(palette.secondary, a2),
          transform: "rotate(-6deg)",
          borderRadius: "0.375rem",
        },
      },
    );
  }

  return { baseStyle, blobs, overlayStyle, geometry, custom };
}
